import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { acceptInvite } from "@/lib/license-api.js";
import { setSession } from "@/lib/session.js";
import { setDek } from "@/lib/dek.js";

type State =
  | { kind: "accepting" }
  | { kind: "error"; message: string }
  | { kind: "done" };

export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: "accepting" });

  useEffect(() => {
    if (!token) {
      setState({ kind: "error", message: "Missing invite token in the link." });
      return;
    }
    (async () => {
      try {
        const result = await acceptInvite(token);
        setSession(result.jwt);
        setDek(result.dek);
        setState({ kind: "done" });
        navigate("/", { replace: true });
      } catch (e) {
        const ee = e as Error & { error?: string };
        setState({ kind: "error", message: prettyError(ee.error ?? ee.message) });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-12">
      <div className="mb-8 text-center">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-900">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-brand-700 text-white text-sm font-semibold">
            AL
          </span>
          <span className="text-lg font-semibold tracking-tight">AgencyLens</span>
        </Link>
      </div>

      {state.kind === "accepting" && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-600">
          Accepting your team invite…
        </div>
      )}

      {state.kind === "error" && (
        <div className="space-y-4 rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-900">
          <div>
            <h2 className="text-base font-semibold">Invite couldn't be accepted</h2>
            <p className="mt-2">{state.message}</p>
          </div>
          <Link
            to="/login"
            className="inline-block rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-900 hover:bg-red-100"
          >
            Go to sign in
          </Link>
        </div>
      )}

      {state.kind === "done" && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-900">
          Invite accepted. Redirecting to the dashboard…
        </div>
      )}
    </div>
  );
}

function prettyError(code: string): string {
  switch (code) {
    case "invalid_or_expired_invite":
      return "This invite is invalid or has expired. Ask the account owner to send a new one (invites expire after 7 days).";
    case "email_unavailable":
      return "That email already has its own AgencyLens account, so it can't join as a team member.";
    case "missing_token":
      return "No invite token in the link.";
    case "dek_not_configured":
      return "The server isn't fully configured yet. Please contact support.";
    default:
      return code.replace(/_/g, " ");
  }
}
